import React, { useState, useEffect } from "react";
import {
  Container,
  VStack,
  Heading,
  Box,
  Grid,
  GridItem,
  Text,
  Button,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Flex,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Alert,
  AlertIcon,
  Badge,
  Spinner,
} from "@chakra-ui/react";
import { useAuth } from "../contexts/AuthContext";
import { useTournament } from "../contexts/TournamentContext";
import { scoresService } from "../services/scoresService";
import { debugAuth } from "../utils/debugAuth";

// Mock data for holes
const mockHoles = Array(18)
  .fill()
  .map((_, i) => ({
    number: i + 1,
    par: 4,
  }));

const LiveScoringPage = () => {
  const { user } = useAuth();
  const {
    teamMappingService,
    readOnly,
    round1Course,
    round2Course,
    label,
    year,
    round1ScoringLocked = false,
    round2ScoringEnabled = true,
  } = useTournament();
  const toast = useToast();
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedHole, setSelectedHole] = useState(1);
  const [strokesInput, setStrokesInput] = useState(4);
  const [scores, setScores] = useState({});
  const [currentRound, setCurrentRound] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userTeam, setUserTeam] = useState(null);

  const isRoundScoringLocked = (round) => {
    if (readOnly) return true;
    if (round === 1) return Boolean(round1ScoringLocked);
    if (round === 2) return round2ScoringEnabled === false;
    return false;
  };

  const getRoundLockMessage = (round) => {
    if (round === 1) {
      return "Round 1 scoring is locked. Scores can no longer be adjusted.";
    }
    return "Round 2 scoring is locked. Scores can no longer be adjusted.";
  };

  const scoringLocked = isRoundScoringLocked(currentRound);

  // Get user's team info
  useEffect(() => {
    if (user?.email) {
      const teamInfo = teamMappingService.getTeamByEmail(user.email);
      setUserTeam(teamInfo);
      if (teamInfo) {
        setSelectedTeam(teamInfo.teamId.toString());
      }
    }
  }, [user]);

  // Load scores and set up real-time listener
  useEffect(() => {
    setLoading(true);

    const unsubscribe = scoresService.onScoresChange(
      (newScores) => {
        setScores(newScores);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading scores:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [year]);

  const handleScoreSubmit = async () => {
    // DEBUG: Log authentication details
    console.log("=== SCORE SUBMIT DEBUG ===");
    debugAuth.logCurrentUser();
    await debugAuth.logIdToken();
    await debugAuth.testFirebaseConnection();
    console.log("Selected Team:", selectedTeam);
    console.log("User Team:", userTeam);
    console.log(
      "Can Edit Team:",
      teamMappingService.canEditTeamScores(user?.email, selectedTeam)
    );
    console.log("========================");

    if (isRoundScoringLocked(currentRound)) {
      toast({
        title: currentRound === 1 ? "Round 1 is locked" : "Round 2 is locked",
        description: getRoundLockMessage(currentRound),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to record scores",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!selectedTeam) {
      toast({
        title: "Error",
        description: "Please select a team",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if user can edit this team's scores
    if (!teamMappingService.canEditTeamScores(user.email, selectedTeam)) {
      toast({
        title: "Access Denied",
        description: "You can only edit scores for your own team",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (strokesInput < 1 || strokesInput > 12) {
      toast({
        title: "Invalid Score",
        description: "Strokes must be between 1 and 12",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      await scoresService.saveScore(
        selectedTeam,
        selectedHole,
        currentRound,
        strokesInput,
        user.email
      );

      toast({
        title: "Score Recorded",
        description: `Recorded ${strokesInput} strokes for hole ${selectedHole} (Round ${currentRound})`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      // Auto-advance to next hole
      if (selectedHole < 18) {
        setSelectedHole((prev) => prev + 1);
      } else {
        // Reset to hole 1 after completing 18
        setSelectedHole(1);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error?.message || "Failed to save score. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const getTeamScore = (teamId, round) => {
    return scoresService.calculateTeamTotal(teamId, round, scores);
  };

  const canEditTeam = (teamId) => {
    return user && teamMappingService.canEditTeamScores(user.email, teamId);
  };

  const renderScorecard = (roundNumber) => {
    const teams = teamMappingService.getAllTeams();

    return (
      <Box overflowX="auto" mb={8}>
        <Heading size="md" mb={4}>
          Round {roundNumber} Scorecard -{" "}
          {roundNumber === 1 ? round1Course : round2Course}
        </Heading>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th color="white">Team</Th>
              {mockHoles.map((hole) => (
                <Th key={hole.number} color="white" isNumeric>
                  {hole.number}
                </Th>
              ))}
              <Th color="white" isNumeric>
                Total
              </Th>
              <Th color="white" isNumeric>
                Holes
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {teams.map((team) => {
              const teamScore = getTeamScore(team.id, roundNumber);
              const isUserTeam = canEditTeam(team.id);

              return (
                <Tr key={team.id} bg={isUserTeam ? "green.800" : "transparent"}>
                  <Td>
                    <Flex align="center" gap={2}>
                      {team.name}
                      {isUserTeam && (
                        <Badge colorScheme="green" size="sm">
                          Your Team
                        </Badge>
                      )}
                    </Flex>
                  </Td>
                  {mockHoles.map((hole) => {
                    const scoreKey = `${team.id}-${hole.number}-${roundNumber}`;
                    const score = scores[scoreKey];
                    return (
                      <Td
                        key={hole.number}
                        isNumeric
                        bg={isUserTeam && score ? "green.700" : "transparent"}
                      >
                        {score != null && score !== "" ? score : "-"}
                      </Td>
                    );
                  })}
                  <Td isNumeric fontWeight="bold">
                    {teamScore.holesPlayed > 0 ? teamScore.total : "-"}
                  </Td>
                  <Td isNumeric fontSize="sm" color="gray.400">
                    {teamScore.holesPlayed}/18
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={6}>
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Loading scores...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={8} align="stretch">
        <Heading size="lg">
          {readOnly ? `${label} Final Scores` : "Live Scoring"}
        </Heading>

        {readOnly && (
          <Alert status="info">
            <AlertIcon />
            <Text fontSize="sm">
              This is the {label} archive. Scores are view-only.
            </Text>
          </Alert>
        )}

        {/* User Team Info */}
        {user && userTeam && !readOnly && (
          <Alert status="info">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Welcome, {userTeam.playerName}!</Text>
              <Text fontSize="sm">
                You're on team: {userTeam.teamName} (with {userTeam.teammate})
              </Text>
            </Box>
          </Alert>
        )}

        {/* Authentication Warning */}
        {!user && !readOnly && (
          <Alert status="info">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Guest Mode</Text>
              <Text fontSize="sm">
                You can view all scores and the leaderboard. To record scores
                for your team, please log in.
              </Text>
            </Box>
          </Alert>
        )}

        {/* Score Input Section */}
        {!readOnly && (
        <Box bg="green.700" p={6} borderRadius="lg">
          {scoringLocked && (
            <Alert status="warning" mb={4} borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">{getRoundLockMessage(currentRound)}</Text>
            </Alert>
          )}
          <Grid templateColumns={{ base: "1fr", md: "repeat(5, 1fr)" }} gap={6}>
            <GridItem>
              <Text mb={2}>Round</Text>
              <Select
                value={currentRound}
                onChange={(e) => setCurrentRound(Number(e.target.value))}
              >
                <option value={1}>Round 1 - {round1Course}</option>
                <option value={2}>Round 2 - {round2Course}</option>
              </Select>
            </GridItem>

            <GridItem>
              <Text mb={2}>Select Team</Text>
              <Select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                placeholder="Choose team"
                disabled={Boolean(userTeam)}
              >
                {teamMappingService.getAllTeams().map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </Select>
            </GridItem>

            <GridItem>
              <Text mb={2}>Hole</Text>
              <Select
                value={selectedHole}
                onChange={(e) => setSelectedHole(Number(e.target.value))}
                isDisabled={scoringLocked}
              >
                {mockHoles.map((hole) => (
                  <option key={hole.number} value={hole.number}>
                    Hole {hole.number}
                  </option>
                ))}
              </Select>
            </GridItem>

            <GridItem>
              <Text mb={2}>Strokes</Text>
              <NumberInput
                min={1}
                max={12}
                value={strokesInput}
                onChange={(value) => setStrokesInput(Number(value))}
                isDisabled={scoringLocked}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </GridItem>

            <GridItem>
              <Text mb={2}>&nbsp;</Text>
              <Button
                colorScheme="green"
                onClick={handleScoreSubmit}
                isLoading={saving}
                loadingText="Saving..."
                isDisabled={!user || !selectedTeam || scoringLocked}
                width="full"
              >
                Record Score
              </Button>
            </GridItem>
          </Grid>
        </Box>
        )}

        {/* Scorecards */}
        <Tabs
          variant="enclosed"
          colorScheme="green"
          index={currentRound - 1}
          onChange={(index) => setCurrentRound(index + 1)}
        >
          <TabList>
            <Tab color="white" _selected={{ color: "white", bg: "green.700" }}>
              Round 1 - {round1Course}
              {!readOnly && round1ScoringLocked ? " (Locked)" : ""}
            </Tab>
            <Tab color="white" _selected={{ color: "white", bg: "green.700" }}>
              Round 2 - {round2Course}
              {!readOnly && round2ScoringEnabled === false ? " (Locked)" : ""}
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel p={0} pt={6}>
              {renderScorecard(1)}
            </TabPanel>
            <TabPanel p={0} pt={6}>
              {renderScorecard(2)}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Container>
  );
};

export default LiveScoringPage;
